import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../store/authContext';
import { toast } from 'sonner';
import brandLogo from '@/assets/logo.png';

export default function AdminLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token, admin } = await adminService.login(username, password, rememberMe);
      login(token, admin);
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.message || 'Greška pri prijavi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-footer flex items-center justify-center px-4">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1707127429206-96ed897291a8?w=1200&auto=format&fit=crop&q=60')`
      }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={brandLogo}
            alt="Fazlić"
            className="h-24 w-auto mx-auto mb-4 object-contain"
          />
          <h1 className="font-serif text-2xl font-bold text-primary-foreground">Admin Panel</h1>
          <p className="text-primary-foreground/60 text-sm font-ui mt-1">Sjenički Proizvodi Fazlić Verzija 1.0</p>
        </div>

        <div className="bg-card rounded-2xl shadow-elevated border border-border p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-8 w-8 rounded-full bg-accent flex items-center justify-center">
              <Lock size={14} className="text-primary" />
            </div>
            <h2 className="font-serif text-base font-semibold text-foreground">Prijavite se</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <Label htmlFor="username" className="font-ui text-sm text-foreground mb-1.5 block">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="font-ui"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-ui text-sm text-foreground mb-1.5 block">Lozinka</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="font-ui pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              <Label htmlFor="rememberMe" className="font-ui text-sm text-muted-foreground">
                Zapamti me na ovom uredjaju
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground hover:bg-primary-light font-ui mt-2"
            >
              {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
              Prijavi se
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
